// Lightweight, reusable page animation helper
// Adds subtle slide / fade-in transitions to key sections across pages

function collectTargets() {
  const selectors = [
    // Home
    '.home .page-header',
    '.home .section',
    '.home .highlights-section',
    '.home .zones-section',
    '.home .tickets-section',
    '.home .trivia-section',
    '.home .news-section',
    '.home .articles-section',

    // Profile
    '.profile-page .avatar-container',
    '.profile-page .user-info',
    '.profile-page .content-card > section',
    '.profile-page .content-card > *',

    // News
    '.news-page .page-header',
    '.news-page .section',
    '.news-page .on-this-day',

    // Fixtures
    '.fixtures-page .page-header',
    '.fixtures-page .section',
    '.fixtures-page .fixtures-sections-wrapper',

    // Select Teams
    '.select-teams-page .pick-header',
    '.select-teams-page .teams-container',
    '.select-teams-page .submit-button-container',

    // Daily Quiz
    '.quiz-app-container .quiz-progress',
    '.quiz-app-container .quiz-question',
    '.quiz-app-container .quiz-options',
    '.quiz-app-container .quiz-actions',
    '.quiz-app-container .quiz-result-modal'
  ];

  const elements = [];
  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      elements.push(el);
    });
  });

  // De-duplicate while preserving order
  return Array.from(new Set(elements));
}

export default function initPageAnimations(router) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const supportsIO = 'IntersectionObserver' in window;
  let observer = null;

  const applyAnimations = () => {
    const targets = collectTargets();
    if (!targets.length) return;

    // No IntersectionObserver support – just show everything with a small stagger
    if (!supportsIO) {
      targets.forEach((el, index) => {
        if (el.dataset.shAnimated) return;
        el.dataset.shAnimated = 'true';
        el.style.setProperty('--sh-anim-delay', `${index * 60}ms`);
        el.classList.add('sh-animate', 'sh-animate--visible');
      });
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            el.classList.add('sh-animate--visible');
            el.dataset.shAnimated = 'true';
            observer.unobserve(el);
          });
        },
        {
          root: null,
          threshold: 0.12
        }
      );
    }

    targets.forEach((el, index) => {
      if (el.dataset.shAnimated) return;
      el.dataset.shAnimated = 'pending';
      el.classList.add('sh-animate');
      el.style.setProperty('--sh-anim-delay', `${index * 60}ms`);
      observer.observe(el);
    });
  };

  const scheduleApply = () => {
    if (typeof requestAnimationFrame !== 'undefined') {
      requestAnimationFrame(() => {
        setTimeout(applyAnimations, 80);
      });
    } else {
      setTimeout(applyAnimations, 120);
    }
  };

  // Initial load
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    scheduleApply();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      scheduleApply();
    }, { once: true });
  }

  // After each route change
  if (router && typeof router.afterEach === 'function') {
    router.afterEach(() => {
      scheduleApply();
    });
  }
}

